// TODO: add tests
import { mandatory, nonMandatory } from './storeFromRequestParamsValidation';

export const createPathWithExtension = (path: string | undefined, extension: string | undefined) => {
  if (!path || !extension) {
    return null;
  }
  return `${path}.${extension}`;
};

export const createImage = (host: string, aspectRatio?: object) => (image: any) => {
  const path = mandatory(image.path, '');
  const fileExtension = mandatory(image.fileExtension, '');
  return {
    host: mandatory(host, ''),
    path: createPathWithExtension(path, fileExtension),
    credit: nonMandatory(image.credit),
    caption: nonMandatory(image.caption),
    alt: nonMandatory(image.alt),
    ...aspectRatio ? { aspectRatio } : {},
  };
};

export const createCoverVideo = (media: any) => {
  const type = nonMandatory(media.type);
  if (type === 'youtube') {
    return {
      html: nonMandatory(media.value.html),
      mediaId: nonMandatory(media.value.mediaId),
      type,
    };
  }
  if (type === 'mmPlayer') {
    return {
      html: mandatory(media.value.html, ''),
      mediaId: mandatory(media.value.mediaId, ''),
      playerId: mandatory(media.value.playerId, ''),
      type,
    };
  }
  if (type === 'jw') {
    return {
      html: mandatory(media.value.html, ''),
      videoId: mandatory(media.value.videoId, ''),
      playerId: mandatory(media.value.playerId, ''),
      type,
    };
  }
  return null;
};

export const createAuthorOldDataStructure = (imagesCDNHost: string) => (author: any) => {
  return {
    name: mandatory(author.name, ''),
    byline: nonMandatory(author.byline),
    link: nonMandatory(author.link),
    image: nonMandatory(author.image, createImage(imagesCDNHost)),
  };
};

const createAuthorPagePath = (editionEndpoint: string) => (path: string) => {
  return `https://${editionEndpoint}${path}`;
};

export const createAuthorsOldDataStructure = (hideOwner: boolean, imagesCDNHost: string) => (authors: any) => {
  const additionalAuthors = nonMandatory(authors.additional, (additional: any) => additional.map(createAuthorOldDataStructure(imagesCDNHost)));
  const owner = mandatory(authors.owner, null, createAuthorOldDataStructure(imagesCDNHost));
  return [
    ...((hideOwner || !owner) ? [] : [owner]),
    ...(additionalAuthors || []),
  ];
};

export const createAuthor = (imagesCDNHost: string, editionEndpoint: string, authorsFullData: any) => (articleAuthor: any) => {
  const defaultAuthor = {
    name: mandatory(articleAuthor.name, ''),
    link: nonMandatory(articleAuthor.link),
  };

  const author = nonMandatory(authorsFullData, (authors: any) => {
    const authorData = authors.find((authorFullData: any) => {
      const authorFullDataUserName = mandatory(authorFullData.userName, '');
      const articleAuthorUserName = mandatory(articleAuthor.username, '');
      return authorFullDataUserName === articleAuthorUserName;
    });

    if (authorData) {
      return {
        name: mandatory(authorData.authorInfo.name.value, ''),
        byline: nonMandatory(authorData.authorInfo.byline.value),
        link: nonMandatory(authorData.pagePath, createAuthorPagePath(editionEndpoint)),
        image: nonMandatory(authorData.authorInfo.image.value, createImage(imagesCDNHost)),
      };
    }
    return defaultAuthor;
  });
  return author || defaultAuthor;
};

export const createAuthors = (hideOwner: boolean, imagesCDNHost: string, editionEndpoint: string, authorsFullData: any) => (articleAuthors: any) => {
  const additionalAuthors = nonMandatory(articleAuthors.additional, (additional: any) => additional.map(createAuthor(imagesCDNHost, editionEndpoint, authorsFullData)));
  const owner = mandatory(articleAuthors.owner, null, createAuthor(imagesCDNHost, editionEndpoint, authorsFullData));
  const authors = [
    ...((hideOwner || !owner) ? [] : [owner]),
    ...(additionalAuthors || []),
  ];
  return authors;
};

const removeSeconds = (time: string) => time.replace(/:\d+\s/gi, ' ');

const DATE_MED = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};
export const createDate = (locale: string, timeZone: string, articleDateISO: string) => {
  const articleDate = Date.parse(articleDateISO);
  return Intl.DateTimeFormat(locale, { timeZone, ...DATE_MED }).format(articleDate);
};

const TIME_WITH_SHORT_OFFSET = {
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  timeZoneName: 'short',
};
export const createTime = (locale: string, timeZone: string, articleDateISO: string) => {
  const articleDate = Date.parse(articleDateISO);
  return removeSeconds(Intl.DateTimeFormat(locale, { timeZone, ...TIME_WITH_SHORT_OFFSET }).format(articleDate));
};

export const createTimeAndDate = (locale: string, timeZone: string, articleDateISO: string) => {
  const articleDate = Date.parse(articleDateISO);
  return removeSeconds(Intl.DateTimeFormat(locale, { timeZone, ...TIME_WITH_SHORT_OFFSET, ...DATE_MED }).format(articleDate));
};


export const createShortDateOrTime = (midnightInTimeZoneISO: string, timeZone: string, locale: string) => (articleDateISO: string) => {
  const articleDate = Date.parse(articleDateISO);
  const midnightInTimeZone = Date.parse(midnightInTimeZoneISO);
  const time = createTime(locale, timeZone, articleDateISO);
  const date = createDate(locale, timeZone, articleDateISO);
  return articleDate >= midnightInTimeZone ? time : date;
};

export const createArticles = (imagesHost: any, midnightInTimeZoneISO: string, timeZone: string, locale: string) => (articles: Array<any>) => {
  return articles.map(article => {
    const hideOwner = nonMandatory(article.metadata.hideOwner);
    return {
      id: mandatory(article.id, ''),
      title: mandatory(article.title, ''),
      image: mandatory(article.cover.image.value, {}, createImage(imagesHost)),
      coverType: mandatory(article.coverType, ''),
      articleUrl: mandatory(article.articleUrl, ''),
      createdAt: mandatory(article.createdAt, '', createShortDateOrTime(midnightInTimeZoneISO, timeZone, locale)),
      updatedAt: mandatory(article.updatedAt, '', createShortDateOrTime(midnightInTimeZoneISO, timeZone, locale)),
      featuredAt: mandatory(article.featuredAt, ''),
      authors: mandatory(article.authors, [], createAuthorsOldDataStructure(hideOwner, imagesHost)),
      description: mandatory(article.seoDescription, ''),
      mainCategory: nonMandatory(article.mainCategory.displayName),
      sponsoredText: nonMandatory(article.metadata.sponsor.text),
      brandName: nonMandatory(article.metadata.sponsor.brandName),
      logo: nonMandatory(article.metadata.sponsor.image),
    };
  });
};
